import { computed, readonly, ref, watch, type ComputedRef } from 'vue';

import { MessageDeliveryStatus } from '@/entities/message';
import {
  createIndexedDBOutgoingQueueStorage,
  createOutgoingQueue,
  OutgoingStatus,
  type OutgoingMessage,
  type OutgoingQueueTransition,
} from '@/features/message/model/outgoing-queue';
import { MessageService, type Message } from '@/shared/api';
import { ConnectionStatus, useConnectionStatus } from '@/shared/lib';

type OutgoingMessageStatusChange = {
  message: OutgoingFacadeMessage;
  serverMessage: Message | null;
  status: MessageDeliveryStatus;
};

type OutgoingFacadeMessage = Omit<OutgoingMessage, 'status'> & {
  status: MessageDeliveryStatus;
};

const toDeliveryStatus = (status: OutgoingMessage['status']): MessageDeliveryStatus => {
  const statuses: Record<OutgoingMessage['status'], MessageDeliveryStatus> = {
    failed: MessageDeliveryStatus.FAILED,
    pending: MessageDeliveryStatus.PENDING,
    sending: MessageDeliveryStatus.SENDING,
  };
  return statuses[status];
};

const toFacadeMessage = (message: OutgoingMessage): OutgoingFacadeMessage => ({
  ...message,
  status: toDeliveryStatus(message.status),
});

const toOutgoingStatus = (status: MessageDeliveryStatus): OutgoingMessage['status'] => {
  const statuses: Partial<Record<MessageDeliveryStatus, OutgoingMessage['status']>> = {
    [MessageDeliveryStatus.FAILED]: OutgoingStatus.FAILED,
    [MessageDeliveryStatus.PENDING]: OutgoingStatus.PENDING,
    [MessageDeliveryStatus.SENDING]: OutgoingStatus.SENDING,
  };
  return statuses[status] ?? OutgoingStatus.PENDING;
};

const facades = new Map<string, ReturnType<typeof createQueueFacade>>();

const createQueueFacade = (senderId: string) => {
  const version = ref<number>(0);

  const queue = createOutgoingQueue(
    senderId,
    createIndexedDBOutgoingQueueStorage(),
    async (message) => {
      const response = await MessageService.sendMessage({
        path: { id: message.chatId },
        body: {
          clientMessageId: message.clientMessageId,
          content: message.content,
          replyToMessageId: message.replyToMessageId,
          type: 'text',
        },
      });
      if (response.error || !response.data) {
        throw response.error;
      }
      return response.data;
    },
  );

  const listeners = new Set<(change: OutgoingMessageStatusChange) => void>();

  const messages: ComputedRef<OutgoingFacadeMessage[]> = computed<OutgoingFacadeMessage[]>(() => {
    void version.value;
    return queue.snapshot().map(toFacadeMessage);
  });

  queue.subscribe((transition: OutgoingQueueTransition): void => {
    version.value += 1;
    const status: MessageDeliveryStatus = transition.serverMessage
      ? MessageDeliveryStatus.SENT
      : toDeliveryStatus(transition.current.status);
    listeners.forEach((listener) =>
      listener({
        message: toFacadeMessage(transition.current),
        serverMessage: transition.serverMessage ?? null,
        status,
      }),
    );
  });

  const { status: connectionStatus } = useConnectionStatus();

  watch(connectionStatus, (status): void => {
    if (status === ConnectionStatus.CONNECTED) {
      void queue.flush();
    }
  });

  return {
    confirm: queue.confirm,
    discard: async (clientMessageId: string): Promise<void> => {
      await queue.discard(clientMessageId);
      version.value += 1;
    },
    enqueue: async (
      input: Omit<OutgoingMessage, 'clientMessageId' | 'createdAt' | 'senderId' | 'status'>,
    ): Promise<OutgoingFacadeMessage> =>
      toFacadeMessage(await queue.enqueue({ ...input, clientMessageId: crypto.randomUUID() })),
    flush: queue.flush,
    hydrate: async (): Promise<OutgoingFacadeMessage[]> =>
      (await queue.hydrate()).map(toFacadeMessage),
    messages: readonly(messages),
    onStatusChange: (listener: (change: OutgoingMessageStatusChange) => void): (() => void) => {
      listeners.add(listener);
      return (): void => {
        listeners.delete(listener);
      };
    },
    retry: queue.retry,
    send: async (message: OutgoingFacadeMessage): Promise<Message | null> =>
      queue.send({ ...message, status: toOutgoingStatus(message.status) }),
  };
};

const useOutgoingMessagesQueue = (senderId: string) => {
  const existing = facades.get(senderId);
  if (existing) {
    return existing;
  }

  const facade = createQueueFacade(senderId);
  facades.set(senderId, facade);
  return facade;
};

const clearOutgoingMessagesQueue = async (senderId: string): Promise<void> => {
  const queue = useOutgoingMessagesQueue(senderId);

  await queue.hydrate();
  await Promise.all(queue.messages.value.map((message) => queue.discard(message.clientMessageId)));
  facades.delete(senderId);
};

export { clearOutgoingMessagesQueue, useOutgoingMessagesQueue };
